import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  Chip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Area,
  AreaChart
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { LeadConversionReport } from '../../types/reports';

interface LeadConversionChartProps {
  data: LeadConversionReport;
}

const LeadConversionChart: React.FC<LeadConversionChartProps> = ({ data }) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  const getDateLocale = () => {
    const lang = i18n.language;
    if (lang === 'pt-BR') return 'pt-BR';
    if (lang === 'es-ES') return 'es-ES';
    return 'en-US';
  };

  const dailyData = data.dailyStats.map(stat => ({
    ...stat,
    date: new Date(stat.date).toLocaleDateString(getDateLocale(), { 
      day: '2-digit', 
      month: '2-digit' 
    })
  }));

  const agentData = data.agentStats.map(stat => ({
    ...stat,
    agentName: stat.agentName.length > 15 
      ? stat.agentName.substring(0, 15) + '...' 
      : stat.agentName
  }));

  const qualityData = data.qualityBreakdown.map(quality => ({
    name: quality.qualityName,
    count: quality.count,
    color: quality.color
  }));

  const statusData = data.statusBreakdown.map(status => ({
    name: status.status,
    count: status.count,
    color: status.color
  }));

  return (
    <Box sx={{ 
      width: '100%', 
      maxWidth: '100%', 
      overflow: 'hidden',
      px: isMobile ? 0 : 0
    }}>
      {/* Summary Cards */}
      <Box sx={{ 
        display: 'flex', 
        flexWrap: 'wrap', 
        gap: isMobile ? 1 : 3, 
        mb: isMobile ? 2 : 4,
        justifyContent: isMobile ? 'center' : 'flex-start'
      }}>
        <Box sx={{ 
          flex: isMobile ? '1 1 45%' : '1 1 200px', 
          minWidth: isMobile ? '45%' : '200px',
          maxWidth: isMobile ? '45%' : 'none'
        }}>
          <Card sx={{ height: isMobile ? '80px' : 'auto' }}>
            <CardContent sx={{ p: isMobile ? 1 : 2 }}>
              <Typography color="textSecondary" gutterBottom variant={isMobile ? "caption" : "body2"}>
                {t('pages.reports.charts.leadConversion.totalLeads')}
              </Typography>
              <Typography variant={isMobile ? "h6" : "h4"} sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                {data.totalLeads}
              </Typography>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ 
          flex: isMobile ? '1 1 45%' : '1 1 200px', 
          minWidth: isMobile ? '45%' : '200px',
          maxWidth: isMobile ? '45%' : 'none'
        }}>
          <Card sx={{ height: isMobile ? '80px' : 'auto' }}>
            <CardContent sx={{ p: isMobile ? 1 : 2 }}>
              <Typography color="textSecondary" gutterBottom variant={isMobile ? "caption" : "body2"}>
                {t('pages.reports.charts.leadConversion.assignedLeads')}
              </Typography>
              <Typography variant={isMobile ? "h6" : "h4"} sx={{ fontWeight: 'bold', color: 'success.main' }}>
                {data.assignedLeads}
              </Typography>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ 
          flex: isMobile ? '1 1 45%' : '1 1 200px', 
          minWidth: isMobile ? '45%' : '200px',
          maxWidth: isMobile ? '45%' : 'none'
        }}>
          <Card sx={{ height: isMobile ? '80px' : 'auto' }}>
            <CardContent sx={{ p: isMobile ? 1 : 2 }}>
              <Typography color="textSecondary" gutterBottom variant={isMobile ? "caption" : "body2"}>
                {t('pages.reports.charts.leadConversion.conversionRate')}
              </Typography>
              <Typography variant={isMobile ? "h6" : "h4"} sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                {data.conversionRate.toFixed(1)}%
              </Typography>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ 
          flex: isMobile ? '1 1 45%' : '1 1 200px', 
          minWidth: isMobile ? '45%' : '200px',
          maxWidth: isMobile ? '45%' : 'none'
        }}>
          <Card sx={{ height: isMobile ? '80px' : 'auto' }}>
            <CardContent sx={{ p: isMobile ? 1 : 2 }}>
              <Typography color="textSecondary" gutterBottom variant={isMobile ? "caption" : "body2"}>
                {t('pages.reports.charts.leadConversion.assignmentRate')}
              </Typography>
              <Typography variant={isMobile ? "h6" : "h4"} sx={{ fontWeight: 'bold', color: 'info.main' }}>
                {data.assignmentRate.toFixed(1)}%
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Charts */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: isMobile ? 2 : 3,
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden'
      }}>
        {/* Daily Leads Chart */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row',
          flexWrap: isMobile ? 'nowrap' : 'wrap', 
          gap: isMobile ? 2 : 3,
          width: '100%',
          maxWidth: '100%'
        }}>
          <Box sx={{ 
            flex: isMobile ? '1 1 100%' : '1 1 600px', 
            minWidth: isMobile ? '100%' : '600px',
            width: '100%',
            maxWidth: '100%'
          }}>
            <Paper sx={{ 
              p: isMobile ? 1 : 3,
              width: '100%',
              maxWidth: '100%',
              overflow: 'hidden'
            }}>
              <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom sx={{ fontWeight: 'bold' }}>
                {t('pages.reports.charts.leadConversion.leadsPerDay')}
              </Typography>
              <ResponsiveContainer width="100%" height={isMobile ? 280 : 300}>
                <LineChart data={dailyData} margin={{ top: 5, right: 30, left: 20, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="totalLeads"
                    stroke={theme.palette.primary.main}
                    strokeWidth={2}
                    name={t('pages.reports.charts.leadConversion.totalLeadsLabel')}
                  />
                  <Line
                    type="monotone"
                    dataKey="assignedLeads"
                    stroke={theme.palette.success.main}
                    strokeWidth={2}
                    name={t('pages.reports.charts.leadConversion.assignedLeadsLabel')}
                  />
                  <Line
                    type="monotone"
                    dataKey="convertedLeads"
                    stroke={theme.palette.warning.main}
                    strokeWidth={2}
                    name={t('pages.reports.charts.leadConversion.convertedLeads')}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Paper>
          </Box>

          {/* Quality Breakdown */}
          <Box sx={{ 
            flex: isMobile ? '1 1 100%' : '1 1 300px', 
            minWidth: isMobile ? '100%' : '300px',
            width: '100%',
            maxWidth: '100%'
          }}>
            <Paper sx={{ 
              p: isMobile ? 1 : 3,
              width: '100%',
              maxWidth: '100%',
              overflow: 'hidden'
            }}>
              <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom sx={{ fontWeight: 'bold' }}>
                {t('pages.reports.charts.leadConversion.leadQuality')}
              </Typography>
              <ResponsiveContainer width="100%" height={isMobile ? 200 : 300}>
                <PieChart>
                  <Pie
                    data={qualityData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, value }) => `${name}: ${value}`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="count"
                  >
                    {qualityData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </Paper>
          </Box>
        </Box>

        {/* Agent Performance */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row',
          flexWrap: isMobile ? 'nowrap' : 'wrap', 
          gap: isMobile ? 2 : 3,
          width: '100%',
          maxWidth: '100%'
        }}>
          <Box sx={{ 
            flex: isMobile ? '1 1 100%' : '1 1 600px', 
            minWidth: isMobile ? '100%' : '600px',
            width: '100%',
            maxWidth: '100%'
          }}>
            <Paper sx={{ 
              p: isMobile ? 1 : 3,
              width: '100%',
              maxWidth: '100%',
              overflow: 'hidden'
            }}>
              <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom sx={{ fontWeight: 'bold' }}>
                {t('pages.reports.charts.leadConversion.agentPerformance')}
              </Typography>
              <ResponsiveContainer width="100%" height={isMobile ? 280 : 300}>
                <BarChart data={agentData} layout="horizontal" margin={{ top: 5, right: 30, left: 20, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="agentName" type="category" width={100} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="totalLeads" fill={theme.palette.primary.main} name={t('pages.reports.charts.leadConversion.totalLeadsBar')} />
                  <Bar dataKey="assignedLeads" fill={theme.palette.success.main} name={t('pages.reports.charts.leadConversion.assignedLeadsBar')} />
                  <Bar dataKey="convertedLeads" fill={theme.palette.warning.main} name={t('pages.reports.charts.leadConversion.convertedLeadsBar')} />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Box>

          {/* Status Breakdown */}
          <Box sx={{ 
            flex: isMobile ? '1 1 100%' : '1 1 300px', 
            minWidth: isMobile ? '100%' : '300px',
            width: '100%',
            maxWidth: '100%'
          }}>
            <Paper sx={{ 
              p: isMobile ? 1 : 3,
              width: '100%',
              maxWidth: '100%',
              overflow: 'hidden'
            }}>
              <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom sx={{ fontWeight: 'bold' }}>
                {t('pages.reports.charts.leadConversion.leadStatus')}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {statusData.map((status, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="body2">{status.name}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {status.count}
                      </Typography>
                      <Chip
                        label={`${((status.count / data.totalLeads) * 100).toFixed(1)}%`}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </Box>
                  </Box>
                ))}
              </Box>
            </Paper>
          </Box>
        </Box>

        {/* Follow-up Stats */}
        {data.followUpStats && (
          <Box sx={{ 
            width: '100%',
            maxWidth: '100%',
            overflow: 'hidden'
          }}>
            <Paper sx={{ 
              p: isMobile ? 1 : 3,
              width: '100%',
              maxWidth: '100%',
              overflow: 'hidden'
            }}>
              <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom sx={{ fontWeight: 'bold' }}>
                {t('pages.reports.charts.leadConversion.followUpStats')}
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: isMobile ? 1 : 3,
                justifyContent: isMobile ? 'center' : 'flex-start'
              }}>
                <Box sx={{ 
                  flex: isMobile ? '1 1 45%' : '1 1 200px', 
                  minWidth: isMobile ? '45%' : '200px',
                  maxWidth: isMobile ? '45%' : 'none'
                }}>
                  <Box textAlign="center">
                    <Typography variant={isMobile ? "h6" : "h4"} sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                      {data.followUpStats.totalFollowUps}
                    </Typography>
                    <Typography variant={isMobile ? "caption" : "body2"} color="text.secondary">
                      {t('pages.reports.charts.leadConversion.totalFollowUps')}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ 
                  flex: isMobile ? '1 1 45%' : '1 1 200px', 
                  minWidth: isMobile ? '45%' : '200px',
                  maxWidth: isMobile ? '45%' : 'none'
                }}>
                  <Box textAlign="center">
                    <Typography variant={isMobile ? "h6" : "h4"} sx={{ fontWeight: 'bold', color: 'success.main' }}>
                      {data.followUpStats.sentFollowUps}
                    </Typography>
                    <Typography variant={isMobile ? "caption" : "body2"} color="text.secondary">
                      {t('pages.reports.charts.leadConversion.sentFollowUps')}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ 
                  flex: isMobile ? '1 1 45%' : '1 1 200px', 
                  minWidth: isMobile ? '45%' : '200px',
                  maxWidth: isMobile ? '45%' : 'none'
                }}>
                  <Box textAlign="center">
                    <Typography variant={isMobile ? "h6" : "h4"} sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                      {data.followUpStats.followUpRate.toFixed(1)}%
                    </Typography>
                    <Typography variant={isMobile ? "caption" : "body2"} color="text.secondary">
                      {t('pages.reports.charts.leadConversion.followUpRate')}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ 
                  flex: isMobile ? '1 1 45%' : '1 1 200px', 
                  minWidth: isMobile ? '45%' : '200px',
                  maxWidth: isMobile ? '45%' : 'none'
                }}>
                  <Box textAlign="center">
                    <Typography variant={isMobile ? "h6" : "h4"} sx={{ fontWeight: 'bold', color: 'info.main' }}>
                      {data.followUpStats.randomFollowUpRate.toFixed(1)}%
                    </Typography>
                    <Typography variant={isMobile ? "caption" : "body2"} color="text.secondary">
                      {t('pages.reports.charts.leadConversion.randomFollowUpRate')}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Paper>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default LeadConversionChart;